import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import LegacyReplaceIncentiveModal from '@fhs-legacy/frontend/src/components/replace-incentive-modal';
import { IncentiveType } from '@fhs-legacy/frontend/src/components/replace-incentive-modal/types';

import { IDialogCallbacks } from '../types';

interface IReplaceIncentiveModalContext {
  show: (callbacks: IDialogCallbacks) => void;
  close: () => void;
}

const ReplaceIncentiveModalContext = createContext<IReplaceIncentiveModalContext | null>(null);

export const useReplaceIncentiveModal = (): IReplaceIncentiveModalContext => {
  const context = useContext(ReplaceIncentiveModalContext);
  if (!context) {
    throw new Error('useReplaceIncentiveModal must be used within a ReplaceIncentiveModalProvider');
  }
  return context;
};

interface IReplaceIncentiveModalProviderProps {
  children: React.ReactNode;
}

export const ReplaceIncentiveModalProvider = ({
  children,
}: IReplaceIncentiveModalProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [callbacks, setCallbacks] = useState<IDialogCallbacks>({});

  const show = useCallback((newCallbacks: IDialogCallbacks) => {
    setCallbacks(newCallbacks);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setCallbacks({});
  }, []);

  const handleConfirm = useCallback(() => {
    callbacks.onConfirm?.();
    close();
  }, [callbacks, close]);

  const handleDismiss = useCallback(() => {
    callbacks.onDismiss?.();
    close();
  }, [callbacks, close]);

  const contextValue = useMemo(() => ({ show, close }), [show, close]);

  return (
    <ReplaceIncentiveModalContext.Provider value={contextValue}>
      {children}
      {isOpen && (
        <LegacyReplaceIncentiveModal
          incentiveType={IncentiveType.INCENTIVE}
          onConfirm={handleConfirm}
          onDismiss={handleDismiss}
          isOpen={isOpen}
        />
      )}
    </ReplaceIncentiveModalContext.Provider>
  );
};
